import { Amplify } from 'aws-amplify';
import { signInWithRedirect } from 'aws-amplify/auth';
import 'aws-amplify/auth/enable-oauth-listener';
import { WITNESS_API_BASE } from './console-env';
import { currentAccessToken } from './access-token';

let configured = false;
let passkeyUrl: string | null = null;
const DESTINATION_KEY = 'witness.tenant.destination';

export async function configureTenantAuth(): Promise<void> {
  if (configured) return;
  if (!WITNESS_API_BASE) throw new Error('Console API is not configured for this hostname.');
  const response = await fetch(`${WITNESS_API_BASE}/tenant/auth/config`);
  if (!response.ok) throw new Error('Tenant sign-in is temporarily unavailable.');
  const config = await response.json() as { userPoolId: string; userPoolClientId: string; domain: string };
  Amplify.configure({ Auth: { Cognito: {
    userPoolId: config.userPoolId,
    userPoolClientId: config.userPoolClientId,
    loginWith: { oauth: {
      domain: config.domain.replace(/^https:\/\//, ''),
      scopes: ['openid', 'email', 'profile'],
      redirectSignIn: [`${window.location.origin}/tenant/callback`],
      redirectSignOut: [`${window.location.origin}/tenant/login`],
      responseType: 'code',
    } },
  } } });
  passkeyUrl = `${config.domain}/passkeys/add?client_id=${encodeURIComponent(config.userPoolClientId)}&redirect_uri=${encodeURIComponent(`${window.location.origin}/tenant/callback`)}`;
  configured = true;
}

export async function beginTenantLogin(destination: string): Promise<void> {
  window.sessionStorage.setItem(DESTINATION_KEY, destination);
  // Re-authenticate even when Amplify retains a user whose Witness session ended.
  await signInWithRedirect({ options: { prompt: 'LOGIN' } });
}

export async function establishTenantSession(): Promise<void> {
  if (!WITNESS_API_BASE) throw new Error('Console API is not configured for this hostname.');
  const token = await currentAccessToken();
  if (!token) throw new Error('Sign-in did not return an access token.');
  const response = await fetch(`${WITNESS_API_BASE}/tenant/sessions`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Could not establish the tenant session.');
}

export function tenantLoginDestination(): string {
  return window.sessionStorage.getItem(DESTINATION_KEY) ?? '/';
}
export function tenantPasskeyEnrollmentUrl(): string {
  if (!passkeyUrl) throw new Error('Tenant auth has not been configured.');
  return passkeyUrl;
}
export async function hasActiveTenantSession(): Promise<boolean> {
  if (!WITNESS_API_BASE) return false;
  const token = await currentAccessToken();
  if (!token) return false;
  const response = await fetch(`${WITNESS_API_BASE}/tenant/sessions`, { headers: { Authorization: `Bearer ${token}` } });
  return response.ok;
}
export async function tenantAuthorizationHeader(): Promise<HeadersInit> {
  const token = await currentAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
